import { useCallback, useEffect, useRef } from 'react'
import { useContentInteraction } from './useContentInteraction'
import { HOVER_THRESHOLD_MS } from './useContentInteraction'
import type { ContentInteraction } from './useContentInteraction'

/**
 * Delay in milliseconds before tracking an impression after an item becomes visible.
 * Used consistently across all impression tracking to ensure users have intentionally viewed content.
 */
export const IMPRESSION_DELAY_MS = 1000

/**
 * Threshold (0-1) for how much of an element must be visible before tracking can begin.
 * 0.9 means 90% of the element must be in the viewport.
 */
export const IMPRESSION_VISIBILITY_THRESHOLD = 0.9

export interface UseCardInteractionsProps<TItem> {
  contentId: string
  item: TItem
  itemIndex: number
  hoverStartTimeRef: React.MutableRefObject<Map<string, number>>
  impressionTimeoutRef: React.MutableRefObject<Map<string, NodeJS.Timeout>>
  hasTrackedImpression: React.MutableRefObject<Set<string>>
  createInteraction: (item: TItem, itemIndex: number) => ContentInteraction | null
  onClickTrack?: () => void
}

export interface UseIsolatedCardInteractionsProps<TItem> {
  contentId: string
  item: TItem
  itemIndex: number
  createInteraction: (item: TItem, itemIndex: number) => ContentInteraction | null
  onClickTrack?: () => void
}

export interface UseCardInteractionsReturn {
  handleMouseEnter: () => void
  handleMouseLeave: () => void
  handleClick: () => void
  handleInViewChange: (inView: boolean) => void
}

export interface UseIsolatedCardInteractionsReturn {
  handleMouseEnter: () => void
  handleMouseLeave: () => void
  handleClick: () => void
}

export function useCardInteractions<TItem>({
  contentId,
  item,
  itemIndex,
  hoverStartTimeRef,
  impressionTimeoutRef,
  hasTrackedImpression,
  createInteraction,
  onClickTrack,
}: UseCardInteractionsProps<TItem>): UseCardInteractionsReturn {
  const { trackInteraction } = useContentInteraction()

  const handleMouseEnter = useCallback(() => {
    if (!contentId) return
    hoverStartTimeRef.current.set(contentId, Date.now())
  }, [contentId, hoverStartTimeRef])

  const handleMouseLeave = useCallback(() => {
    if (!contentId) return

    const startTime = hoverStartTimeRef.current.get(contentId)
    if (!startTime) return

    const dwellTime = Date.now() - startTime

    if (dwellTime >= HOVER_THRESHOLD_MS) {
      const interaction = createInteraction(item, itemIndex)
      if (interaction) {
        trackInteraction(interaction.withHover(dwellTime))
      }
    }

    hoverStartTimeRef.current.delete(contentId)
  }, [contentId, hoverStartTimeRef, createInteraction, item, itemIndex, trackInteraction])

  const handleClick = useCallback(() => {
    // Allow for optional additional tracking (legacy analytics events)
    onClickTrack?.()

    const interaction = createInteraction(item, itemIndex)
    if (interaction) {
      trackInteraction(interaction.withPress())
    }
  }, [createInteraction, item, itemIndex, trackInteraction, onClickTrack])

  const handleInViewChange = useCallback(
    (inView: boolean) => {
      if (!contentId) return

      if (inView) {
        // Prevent duplicate timers for the same content
        if (impressionTimeoutRef.current.has(contentId) || hasTrackedImpression.current.has(contentId)) {
          return
        }

        const timeout = setTimeout(() => {
          // Double-check still hasn't been tracked (in case of race conditions)
          if (hasTrackedImpression.current.has(contentId)) return
          hasTrackedImpression.current.add(contentId)

          const interaction = createInteraction(item, itemIndex)
          if (interaction) {
            trackInteraction(interaction.withImpression())
          }

          // Clean up the timeout reference after tracking
          impressionTimeoutRef.current.delete(contentId)
        }, IMPRESSION_DELAY_MS)

        impressionTimeoutRef.current.set(contentId, timeout)
      } else {
        const timeout = impressionTimeoutRef.current.get(contentId)
        if (timeout) {
          clearTimeout(timeout)
          impressionTimeoutRef.current.delete(contentId)
        }
      }
    },
    [contentId, hasTrackedImpression, createInteraction, item, itemIndex, trackInteraction, impressionTimeoutRef],
  )

  return {
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
    handleInViewChange,
  }
}

export function useIsolatedCardInteractions<TItem>({
  contentId,
  item,
  itemIndex,
  createInteraction,
  onClickTrack,
}: UseIsolatedCardInteractionsProps<TItem>): UseIsolatedCardInteractionsReturn {
  const { trackInteraction } = useContentInteraction()

  // Internal state for hover tracking
  const hoverStartTimeRef = useRef<number | null>(null)

  const handleMouseEnter = useCallback(() => {
    if (!contentId) return
    hoverStartTimeRef.current = Date.now()
  }, [contentId])

  const handleMouseLeave = useCallback(() => {
    if (!contentId || !hoverStartTimeRef.current) return

    const dwellTime = Date.now() - hoverStartTimeRef.current

    if (dwellTime >= HOVER_THRESHOLD_MS) {
      const interaction = createInteraction(item, itemIndex)
      if (interaction) {
        trackInteraction(interaction.withHover(dwellTime))
      }
    }

    hoverStartTimeRef.current = null
  }, [contentId, createInteraction, item, itemIndex, trackInteraction])

  const handleClick = useCallback(() => {
    onClickTrack?.()

    const interaction = createInteraction(item, itemIndex)
    if (interaction) {
      trackInteraction(interaction.withPress())
    }
  }, [createInteraction, item, itemIndex, trackInteraction, onClickTrack])

  return {
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
  }
}

/**
 * Hook specifically for impression tracking with intersection observer.
 * Separates impression logic for cards that handle it via useIntersectionObserver.
 *
 * @example
 * ```tsx
 * const [cardRef, isVisible] = useIntersectionObserver({ threshold: 0.9 })
 * useImpressionTracking({
 *   isVisible,
 *   contentId: item.id,
 *   item,
 *   itemIndex,
 *   createInteraction,
 * })
 * ```
 */
export function useImpressionTracking<TItem>({
  isVisible,
  contentId,
  item,
  itemIndex,
  createInteraction,
}: {
  isVisible: boolean
  contentId: string
  item: TItem
  itemIndex: number
  createInteraction: (item: TItem, itemIndex: number) => ContentInteraction | null
}) {
  const { trackInteraction } = useContentInteraction()
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedImpression = useRef(false)

  // Track impression when the card becomes visible for at least 1 second
  useEffect(() => {
    if (!contentId || hasTrackedImpression.current) return

    if (isVisible) {
      if (impressionTimeoutRef.current) return // Already has pending timer

      impressionTimeoutRef.current = setTimeout(() => {
        if (hasTrackedImpression.current) return
        hasTrackedImpression.current = true

        const interaction = createInteraction(item, itemIndex)
        if (interaction) {
          trackInteraction(interaction.withImpression())
        }

        impressionTimeoutRef.current = null
      }, IMPRESSION_DELAY_MS)
    } else {
      // Clear timeout if item becomes not visible before delay
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }
  }, [isVisible, contentId, item, itemIndex, createInteraction, trackInteraction])
}

export interface UseCollectionImpressionTrackingReturn {
  /** Ref for tracking hover start times per content ID */
  hoverStartTimeRef: React.MutableRefObject<Map<string, number>>
  /** Ref for storing pending impression timeouts per content ID */
  impressionTimeoutRef: React.MutableRefObject<Map<string, NodeJS.Timeout>>
  /** Ref for tracking which content IDs have already had impressions tracked */
  hasTrackedImpression: React.MutableRefObject<Set<string>>
  /** Resets all tracking state (useful when content changes) */
  resetTracking: () => void
}

/**
 * Hook for managing impression tracking state across a collection of items.
 * Consolidates the common pattern of setting up refs and cleanup for collection-level tracking.
 *
 * Use this hook in collection components (rails, grids) and pass the refs to child card components
 * that use `useCardInteractions`.
 *
 * @example
 * ```tsx
 * function MyCollection({ items, containerPosition }) {
 *   const {
 *     hoverStartTimeRef,
 *     impressionTimeoutRef,
 *     hasTrackedImpression,
 *   } = useCollectionImpressionTracking()
 *
 *   const createInteraction = useCallback((item, itemIndex) => {
 *     return new DiscoverItemInteraction({ id: item.id, __typename: item.__typename, attribution: null })
 *       .withSurface('discover')
 *       .withPlacement('collection', containerPosition, itemIndex, 'horizontal_swimlane')
 *   }, [containerPosition])
 *
 *   return items.map((item, index) => (
 *     <MyCard
 *       key={item.id}
 *       item={item}
 *       itemIndex={index}
 *       hoverStartTimeRef={hoverStartTimeRef}
 *       impressionTimeoutRef={impressionTimeoutRef}
 *       hasTrackedImpression={hasTrackedImpression}
 *       createInteraction={createInteraction}
 *     />
 *   ))
 * }
 * ```
 */
export function useCollectionImpressionTracking(): UseCollectionImpressionTrackingReturn {
  const hoverStartTimeRef = useRef<Map<string, number>>(new Map())
  const impressionTimeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const hasTrackedImpression = useRef<Set<string>>(new Set())

  // Cleanup pending impression timeouts on unmount to prevent tracking after navigation
  useEffect(() => {
    const timeoutRef = impressionTimeoutRef.current
    return () => {
      timeoutRef.forEach((timeout) => clearTimeout(timeout))
      timeoutRef.clear()
    }
  }, [])

  const resetTracking = useCallback(() => {
    hoverStartTimeRef.current.clear()
    impressionTimeoutRef.current.forEach((timeout) => clearTimeout(timeout))
    impressionTimeoutRef.current.clear()
    hasTrackedImpression.current.clear()
  }, [])

  return {
    hoverStartTimeRef,
    impressionTimeoutRef,
    hasTrackedImpression,
    resetTracking,
  }
}

export interface UseSingleImpressionTrackingProps {
  /** Callback to execute when impression is tracked */
  onImpression: () => void
  /** Delay in ms before tracking impression after item becomes visible. Defaults to 1000. */
  delay?: number
}

export interface UseSingleImpressionTrackingReturn {
  /** Handler for InView component's onChange prop */
  handleInViewChange: (inView: boolean) => void
  /** Manually trigger impression tracking (respects once-only rule) */
  trackImpression: () => void
  /** Whether the impression has already been tracked */
  hasTracked: boolean
}

/**
 * Hook for tracking a single item's impression with automatic cleanup.
 * Ideal for featured content, hero banners, or any single-item impression tracking.
 *
 * @example
 * ```tsx
 * function FeaturedBanner({ content }) {
 *   const { trackInteraction } = useContentInteraction()
 *
 *   const interaction = useMemo(() => (
 *     new DiscoverItemInteraction({ id: content.id, __typename: content.__typename, attribution: null })
 *       .withSurface('discover')
 *       .withPlacement('featured', 0, 0, 'single_feature')
 *   ), [content])
 *
 *   const { handleInViewChange } = useSingleImpressionTracking({
 *     onImpression: () => trackInteraction(interaction.withImpression()),
 *   })
 *
 *   return (
 *     <InView onChange={handleInViewChange} threshold={IMPRESSION_VISIBILITY_THRESHOLD} triggerOnce>
 *       <BannerContent />
 *     </InView>
 *   )
 * }
 * ```
 */
export function useSingleImpressionTracking({
  onImpression,
  delay = IMPRESSION_DELAY_MS,
}: UseSingleImpressionTrackingProps): UseSingleImpressionTrackingReturn {
  const hasTrackedImpressionRef = useRef(false)
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }
  }, [])

  const trackImpression = useCallback(() => {
    if (hasTrackedImpressionRef.current) return
    hasTrackedImpressionRef.current = true
    onImpression()
  }, [onImpression])

  const handleInViewChange = useCallback(
    (inView: boolean) => {
      if (inView) {
        // Prevent duplicate timers
        if (impressionTimeoutRef.current || hasTrackedImpressionRef.current) {
          return
        }

        impressionTimeoutRef.current = setTimeout(() => {
          trackImpression()
          impressionTimeoutRef.current = null
        }, delay)
      } else {
        // Clear timeout if item becomes not visible before delay
        if (impressionTimeoutRef.current) {
          clearTimeout(impressionTimeoutRef.current)
          impressionTimeoutRef.current = null
        }
      }
    },
    [delay, trackImpression],
  )

  return {
    handleInViewChange,
    trackImpression,
    get hasTracked() {
      return hasTrackedImpressionRef.current
    },
  }
}
